import classNames from 'classnames';
import React, { SVGProps } from 'react';
import { Link } from 'react-router-dom';
import Icon, { IconSize } from '../SVG/Icon';
import './Button.scss';
import Loading from '../Loading/Loading';

export enum ButtonType {
  Primary = 'primary',
  Secondary = 'secondary',
  Cancel = 'cancel',
  Selection = 'selection',
  ListItem = 'list-item',
  Danger = 'danger',
  Warning = 'warning',
  Subtle = 'subtle',
  Icon = 'icon',
}

export enum ButtonIconPlacement {
  Left,
  Right,
}

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  buttonType?: ButtonType;
  className?: string;
  children?: React.ReactNode;
  dataId?: string;
  icon?: React.FC<SVGProps<SVGSVGElement> & { title?: string | undefined }>;
  iconSize?: IconSize;
  iconRotation?: number;
  iconPlacement?: ButtonIconPlacement;
  fullWidth?: boolean;
  outline?: boolean;
  animated?: boolean;
  to?: string;
  loading?: boolean;
  spinnerClassName?: string;
  disabled?: boolean;
  ref?: any;
}

const Button: React.FunctionComponent<ButtonProps> = ({
  className,
  buttonType = ButtonType.Primary,
  children,
  dataId,
  icon,
  iconPlacement,
  iconSize,
  iconRotation,
  fullWidth,
  outline,
  animated,
  loading,
  spinnerClassName,
  to,
  disabled,
  ref,
  ...rest
}) => {
  const classes = classNames('button', className, {
    'button--primary': buttonType === ButtonType.Primary,
    'button--secondary': buttonType === ButtonType.Secondary,
    'button--cancel': buttonType === ButtonType.Cancel,
    'button--selection': buttonType === ButtonType.Selection,
    'button--list-item': buttonType === ButtonType.ListItem,
    'button--danger': buttonType === ButtonType.Danger,
    'button--warning': buttonType === ButtonType.Warning,
    'button--subtle': buttonType === ButtonType.Subtle,
    'button--has-icon': !!icon,
    'button--icon': buttonType === ButtonType.Icon,
    'button--fullWidth': !!fullWidth,
    [`button--${buttonType}--outline`]: !!outline,
    'button--animated': !!animated,
  });

  return to && !disabled ? (
    <Link data-id={dataId} className={classes} to={to}>
      {icon && (iconPlacement === ButtonIconPlacement.Left || buttonType === ButtonType.Icon) && (
        <Icon component={icon} size={iconSize} rotation={iconRotation} className="button__icon" />
      )}
      {children && <span className="button__content">{children}</span>}

      {icon && iconPlacement === ButtonIconPlacement.Right && (
        <Icon component={icon} size={iconSize} rotation={iconRotation} className="button__icon button__icon--right" />
      )}
    </Link>
  ) : (
    <button ref={ref} data-id={dataId} type="button" className={classes} disabled={disabled} {...rest}>
      {loading ? (
        <Loading spinnerClassName={spinnerClassName} size="xs" />
      ) : (
        <>
          {icon && (iconPlacement === ButtonIconPlacement.Left || buttonType === ButtonType.Icon) && (
            <Icon component={icon} size={iconSize} rotation={iconRotation} className="button__icon" />
          )}
          {children && <span className="button__content">{children}</span>}

          {icon && iconPlacement === ButtonIconPlacement.Right && (
            <Icon component={icon} size={iconSize} rotation={iconRotation} className="button__icon button__icon--right" />
          )}
        </>
      )}
    </button>
  );
};

export default Button;
